import { BoxProps } from '@chakra-ui/react';
import { IconSize } from '@common/react/icons';
import { FC, forwardRef, Ref } from 'react';
import {
    MotionPath,
    MotionPathProps,
    MotionSVG,
    MotionSVGProps,
} from '../Motion';

export interface IconSVGComponentProps extends MotionSVGProps {
    size?: IconSize | MotionSVGProps['MotionSVGProps'];
    pathProps?: MotionPathProps;
}

export type IconComponent = FC<IconSVGComponentProps>;

const ICON_SIZE_PROPS: Record<IconSize, { strokeWidth: string; size: string }> =
    {
        lg: { strokeWidth: '2', size: '24px' },
        md: { strokeWidth: '1.8', size: '20px' },
        sm: { strokeWidth: '2.25', size: '16px' },
        xs: { strokeWidth: '2', size: '12px' },
    };
const DEFAULT_SIZE_PROPS = ICON_SIZE_PROPS['md'];

export const isIconSize = (size?: string | BoxProps['w']): size is IconSize => {
    return (
        !!size &&
        (size === 'lg' || size === 'md' || size === 'sm' || size === 'xs')
    );
};

const IconSVG: FC<MotionSVGProps> = forwardRef<SVGSVGElement, MotionSVGProps>(
    (
        {
            size = 'md',
            height,
            width,
            fill,
            strokeWidth,
            stroke,
            children,
            viewBox,
            ...props
        },
        ref: Ref<SVGSVGElement>
    ) => {
        const sizeProps = isIconSize(size) ? ICON_SIZE_PROPS[size] : undefined;

        return (
            <MotionSVG
                width={
                    width ?? sizeProps?.size ?? size ?? DEFAULT_SIZE_PROPS.size
                }
                height={
                    height ?? sizeProps?.size ?? size ?? DEFAULT_SIZE_PROPS.size
                }
                viewBox={viewBox ?? '0 0 24 24'}
                strokeWidth={
                    strokeWidth ??
                    sizeProps?.strokeWidth ??
                    DEFAULT_SIZE_PROPS.strokeWidth
                }
                fill={fill ?? 'none'}
                stroke={stroke ?? 'black'}
                xmlns="http://www.w3.org/2000/svg"
                ref={ref}
                display="inline-block"
                verticalAlign="middle"
                flexShrink={0}
                flexGrow={0}
                {...props}
            >
                {children}
            </MotionSVG>
        );
    }
);
IconSVG.displayName = 'IconSVG';

const IconPath: FC<MotionPathProps> = forwardRef<
    SVGPathElement,
    MotionPathProps
>((props, ref: Ref<SVGPathElement>) => {
    return (
        <MotionPath
            {...props}
            stroke={props.stroke ? props.stroke : 'currentColor'}
            strokeWidth={props.strokeWidth ? props.strokeWidth : 'inherit'}
            strokeLinecap={props.strokeLinecap ? props.strokeLinecap : 'round'}
            strokeLinejoin={
                props.strokeLinejoin ? props.strokeLinejoin : 'round'
            }
            ref={ref}
        />
    );
});
IconPath.displayName = 'IconPath';

export { IconPath, IconSVG };
